import cx from 'classnames';
import './KpiCard.css';

/**
 * QHS KpiCard v2
 * --------------
 * Compact at-a-glance metric tile for analytics dashboards. Big number
 * is the focal point; label sits above as a small uppercase eyebrow;
 * an optional delta line lives below.
 *
 *   <KpiCard label="Patients" value={253} tone="brand" />
 *   <KpiCard label="Alerts"   value={32}  tone="warning"
 *            delta={{ value: '+12%', trend: 'up', label: 'vs last month' }} />
 *   <KpiCard label="Patients" loading />            // skeleton state
 *
 * Tones: 'brand' (default) | 'success' | 'warning' | 'danger' | 'muted'
 * Sizes: 'md' (default) | 'lg' | 'xl'
 *
 * `delta.trend`:
 *   'up'   — green arrow, brand-good
 *   'down' — red arrow, danger
 *   'flat' — grey line, neutral
 *   `goodWhenDown` flips the colour mapping for metrics where lower is
 *   better (e.g. alerts going down is a "success up").
 *
 * Optional additive props (v2.1):
 *   `unit`        — short unit label rendered after the value (e.g. "%")
 *   `contextLine` — secondary line between value and delta
 *   `vizSlot`     — decorative React node rendered as a flex sibling
 *                   (e.g. a mini-donut, sparkline). Triggers
 *                   `qhs-kpi--with-viz` layout. The wrapper applies
 *                   aria-hidden="true" since the visualisation is meant
 *                   to reinforce the value/context — consumers must NOT
 *                   rely on the slot to convey information that is not
 *                   already present in the value or contextLine.
 */
const TrendIcon = ({ trend }) => {
  if (trend === 'up') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="6 14 12 8 18 14" />
      </svg>
    );
  }
  if (trend === 'down') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="6 10 12 16 18 10" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="6" y1="12" x2="18" y2="12" />
    </svg>
  );
};

const computeDeltaTone = (trend, goodWhenDown) => {
  if (trend === 'flat' || !trend) return 'neutral';
  const isGood =
    (trend === 'up' && !goodWhenDown) || (trend === 'down' && goodWhenDown);
  return isGood ? 'good' : 'bad';
};

const KpiCard = ({
  label,
  value,
  unit,
  contextLine,
  vizSlot,
  delta,
  tone = 'brand',
  size = 'md',
  loading = false,
  className,
  ...rest
}) => {
  const deltaTone = delta ? computeDeltaTone(delta.trend, delta.goodWhenDown) : null;
  const hasViz = vizSlot != null;

  return (
    <div
      className={cx(
        'qhs-kpi',
        `qhs-kpi--${tone}`,
        `qhs-kpi--${size}`,
        loading && 'qhs-kpi--loading',
        hasViz && 'qhs-kpi--with-viz',
        className
      )}
      {...rest}
    >
      <div className="qhs-kpi__meta">
        <div className="qhs-kpi__label">{label}</div>
        <div className="qhs-kpi__value">
          {loading ? (
            <span className="qhs-kpi__skeleton qhs-shimmer" aria-hidden="true" />
          ) : (
            <>
              {value}
              {unit != null && <span className="qhs-kpi__unit">{unit}</span>}
            </>
          )}
        </div>
        {contextLine != null && !loading && (
          <div className="qhs-kpi__context">{contextLine}</div>
        )}
        {delta && !loading && (
          <div className={cx('qhs-kpi__delta', `qhs-kpi__delta--${deltaTone}`)}>
            <span className="qhs-kpi__delta-arrow">
              <TrendIcon trend={delta.trend} />
            </span>
            <span className="qhs-kpi__delta-value">{delta.value}</span>
            {delta.label && <span className="qhs-kpi__delta-label">{delta.label}</span>}
          </div>
        )}
      </div>
      {hasViz && (
        <div className="qhs-kpi__viz" aria-hidden="true">
          {vizSlot}
        </div>
      )}
    </div>
  );
};

export default KpiCard;
